import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function PaymentModal({ isOpen, onClose, subscription, upgradePlan, subscriptionData, organizationId, isRenewal = false, onSuccess }) {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Determine if this is a new subscription, upgrade, or renewal
  const isNewSubscription = !subscription && subscriptionData
  const isRenewalFlow = isRenewal && subscription && !upgradePlan

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      billing_cycle: 'monthly',
      payment_method: 'manual',
      transaction_id: '',
      notes: '',
      mark_completed: false
    }
  })

  const selectedBillingCycle = watch('billing_cycle')
  const markCompleted = watch('mark_completed')

  // Calculate amount based on upgrade plan (or current plan for renewal) and selected billing cycle
  const calculateAmount = () => {
    // For renewal, use subscription's current plan
    const plan = isRenewalFlow ? {
      monthly_price: subscription?.monthly_price || 0,
      yearly_price: subscription?.yearly_price || 0,
      currency: subscription?.currency || 'USD'
    } : upgradePlan
    
    if (!plan) return 0
    
    // Check if this is an Enterprise plan with custom price
    if (plan.is_enterprise && plan.custom_price) {
      return parseFloat(plan.custom_price) || 0
    }
    
    let basePrice = 0
    if (selectedBillingCycle === 'monthly') {
      basePrice = parseFloat(plan.monthly_price) || 0
    } else if (selectedBillingCycle === 'yearly') {
      basePrice = parseFloat(plan.yearly_price) || 0
    }
    
    // If per_user pricing, multiply by user count
    // For new subscriptions, use subscriptionData.user_limit
    // For upgrades/renewals, use subscription.user_limit
    const userLimit = isNewSubscription ? subscriptionData?.user_limit : subscription?.user_limit
    const pricingModel = isNewSubscription ? subscriptionData?.pricing_model : subscription?.pricing_model
    
    if (pricingModel === 'per_user' && userLimit) {
      return basePrice * userLimit
    }
    
    return basePrice
  }

  const calculatedAmount = calculateAmount()

  // Format price with currency
  const formatPrice = (price, currency = 'USD', billingCycle = 'monthly') => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price)
    
    return `${formatted} / ${billingCycle === 'yearly' ? 'year' : 'month'}`
  }

  // Get billing cycle options with prices
  const getBillingCycleOptions = () => {
    // For renewal, use subscription's current plan
    const plan = isRenewalFlow ? {
      monthly_price: subscription?.monthly_price || 0,
      yearly_price: subscription?.yearly_price || 0,
      currency: subscription?.currency || 'USD'
    } : upgradePlan
    
    if (!plan) return []
    
    const options = []
    const currency = plan.currency || 'USD'
    
    if (plan.monthly_price > 0) {
      const userLimit = isNewSubscription ? subscriptionData?.user_limit : subscription?.user_limit
      const pricingModel = isNewSubscription ? subscriptionData?.pricing_model : subscription?.pricing_model
      const monthlyPrice = pricingModel === 'per_user' && userLimit
        ? parseFloat(plan.monthly_price) * userLimit
        : parseFloat(plan.monthly_price)
      
      options.push({
        value: 'monthly',
        label: `Monthly - ${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency
        }).format(monthlyPrice)}/month`,
        price: monthlyPrice
      })
    }
    
    if (plan.yearly_price > 0) {
      const userLimit = isNewSubscription ? subscriptionData?.user_limit : subscription?.user_limit
      const pricingModel = isNewSubscription ? subscriptionData?.pricing_model : subscription?.pricing_model
      const yearlyPrice = pricingModel === 'per_user' && userLimit
        ? parseFloat(plan.yearly_price) * userLimit
        : parseFloat(plan.yearly_price)
      
      options.push({
        value: 'yearly',
        label: `Yearly - ${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency
        }).format(yearlyPrice)}/year`,
        price: yearlyPrice
      })
    }
    
    return options
  }

  const billingCycleOptions = getBillingCycleOptions()

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen && upgradePlan) {
      // Set default billing cycle to subscription's current cycle, subscriptionData billing cycle, or monthly
      const defaultCycle = subscription?.billing_cycle || subscriptionData?.billing_cycle || 'monthly'
      reset({
        billing_cycle: defaultCycle,
        payment_method: 'manual',
        transaction_id: '',
        notes: '',
        mark_completed: true // Default to checked for upgrades/new subscriptions since subscription will be created/upgraded immediately
      })
    }
  }, [isOpen, upgradePlan, subscription, subscriptionData, reset])

  const paymentMutation = useMutation(
    (data) => api.post('/payments', data).then(r => r.data),
    {
      onSuccess: (data) => {
        toast.success('Payment created successfully!')
        queryClient.invalidateQueries(['subscriptions'])
        queryClient.invalidateQueries(['payments'])
        onSuccess?.(data)
        onClose()
      },
      onError: (error) => {
        console.error('Payment error:', error)
        toast.error(error.response?.data?.message || 'Failed to create payment')
      }
    }
  )

  // Mutation to create new subscription
  const subscriptionCreateMutation = useMutation(
    (data) => api.post('/subscriptions', { ...data, organization_id: organizationId }).then(r => r.data),
    {
      onError: (error) => {
        console.error('Subscription creation error:', error)
        toast.error(error.response?.data?.message || 'Failed to create subscription')
      }
    }
  )

  // Mutation to update subscription plan
  const subscriptionUpdateMutation = useMutation(
    ({ subscriptionId, planId, billingCycle, status }) => 
      api.put(`/subscriptions/${subscriptionId}`, { 
        plan_id: planId,
        billing_cycle: billingCycle,
        status: status || 'active'
      }).then(r => r.data),
    {
      onError: (error) => {
        console.error('Subscription update error:', error)
        toast.error(error.response?.data?.message || 'Failed to update subscription plan')
      }
    }
  )

  // Mutation to renew subscription
  const renewSubscriptionMutation = useMutation(
    ({ subscriptionId, billingCycle }) => 
      api.post(`/subscriptions/${subscriptionId}/renew`, { billing_cycle: billingCycle }).then(r => r.data),
    {
      onError: (error) => {
        console.error('Renewal error:', error)
        toast.error(error.response?.data?.message || 'Failed to renew subscription')
      }
    }
  )

  const onSubmit = async (data) => {
    // For renewal, subscription is required but no upgradePlan
    if (isRenewalFlow) {
      if (!subscription) {
        toast.error('Subscription is required')
        return
      }
    } else {
      // For upgrades/new subscriptions, upgradePlan is required
      if (!upgradePlan) {
        toast.error('Plan is required')
        return
      }

      // For new subscriptions, subscriptionData is required
      if (isNewSubscription && !subscriptionData) {
        toast.error('Subscription data is required')
        return
      }

      // For upgrades, subscription is required
      if (!isNewSubscription && !subscription) {
        toast.error('Subscription is required')
        return
      }
    }

    setIsSubmitting(true)
    try {
      const amount = calculatedAmount
      
      if (amount <= 0) {
        toast.error('Invalid amount. Please select a valid billing cycle.')
        setIsSubmitting(false)
        return
      }

      let createdSubscription = null

      if (isNewSubscription) {
        // Create new subscription first (preserve user-selected status: active, inactive, suspended)
        const subscriptionPayload = {
          ...subscriptionData,
          billing_cycle: data.billing_cycle,
          status: subscriptionData?.status ?? 'active'
        }
        createdSubscription = await subscriptionCreateMutation.mutateAsync(subscriptionPayload)
        
        // Then create the payment record
        // For new subscriptions, payment is always completed (subscription is created after payment)
        const paymentData = {
          subscription_id: createdSubscription.data.subscription.id,
          organization_id: organizationId,
          plan_id: upgradePlan.id,
          amount: amount,
          currency: upgradePlan.currency || 'USD',
          payment_method: data.payment_method,
          payment_status: 'completed', // Always completed for new subscriptions
          transaction_id: data.transaction_id || null,
          notes: data.notes || null
        }

        await paymentMutation.mutateAsync(paymentData)
        
        // Invalidate queries to refresh UI
        queryClient.invalidateQueries(['subscriptions'])
        queryClient.invalidateQueries(['payments'])
        
        // Success message
        toast.success('Subscription created successfully!')
        
        // Call onSuccess with subscription data
        onSuccess?.(createdSubscription)
      } else if (isRenewalFlow) {
        // Renewal flow
        // First, renew the subscription (resets created_at and updates billing cycle)
        await renewSubscriptionMutation.mutateAsync({
          subscriptionId: subscription.id,
          billingCycle: data.billing_cycle
        })

        // Then create the payment record
        const paymentData = {
          subscription_id: subscription.id,
          organization_id: subscription.organization_id,
          plan_id: subscription.plan_id,
          amount: amount,
          currency: subscription.currency || 'USD',
          payment_method: data.payment_method,
          payment_status: data.mark_completed === false ? 'pending' : 'completed', // Default to completed for renewals
          transaction_id: data.transaction_id || null,
          notes: data.notes || null
        }

        await paymentMutation.mutateAsync(paymentData)
        
        // Invalidate queries to refresh UI
        queryClient.invalidateQueries(['subscriptions'])
        queryClient.invalidateQueries(['payments'])
        
        // Success message
        toast.success('Subscription renewed successfully!')
        
        // Call onSuccess with payment data
        onSuccess?.(paymentData)
      } else {
        // Existing subscription - upgrade flow
        // First, update the subscription plan to complete the upgrade immediately
        await subscriptionUpdateMutation.mutateAsync({
          subscriptionId: subscription.id,
          planId: upgradePlan.id,
          billingCycle: data.billing_cycle,
          status: 'active' // Ensure subscription is active
        })

        // Then create the payment record
        // Since subscription is already upgraded, mark payment as completed by default
        // User can uncheck the checkbox if they want to mark it as pending for manual review
        const paymentData = {
          subscription_id: subscription.id,
          organization_id: subscription.organization_id,
          plan_id: upgradePlan.id,
          amount: amount,
          currency: upgradePlan.currency || 'USD',
          payment_method: data.payment_method,
          payment_status: data.mark_completed === false ? 'pending' : 'completed', // Default to completed for upgrades
          transaction_id: data.transaction_id || null,
          notes: data.notes || null
        }

        await paymentMutation.mutateAsync(paymentData)
        
        // Success message
        toast.success('Subscription upgraded successfully!')
        
        // Call onSuccess with payment data
        onSuccess?.(paymentData)
      }
    } catch (error) {
      // Error handled in mutation onError
      console.error('Payment processing error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>{isRenewalFlow ? 'Renew Subscription' : 'Complete Payment'}</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {/* Plan Summary Section - Light Blue Background */}
          {(upgradePlan || isRenewalFlow) && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">
                    {isRenewalFlow 
                      ? 'Renewing subscription for:' 
                      : isNewSubscription 
                        ? 'Creating subscription for:' 
                        : 'Upgrading to:'}
                  </span>
                  <span className="font-medium text-blue-900">
                    {isRenewalFlow ? subscription?.plan_name : upgradePlan?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">Billing Cycle:</span>
                  <span className="font-medium text-blue-900 capitalize">{selectedBillingCycle || 'monthly'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">Total Amount:</span>
                  <span className="font-medium text-blue-900">
                    {calculatedAmount > 0 
                      ? formatPrice(calculatedAmount, (isRenewalFlow ? subscription?.currency : upgradePlan?.currency) || 'USD', selectedBillingCycle)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Billing Cycle */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Billing Cycle <span className="text-red-500">*</span>
              </label>
              <select
                {...register('billing_cycle', { required: 'Billing cycle is required' })}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSubmitting}
              >
                {billingCycleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.billing_cycle && (
                <p className="mt-1 text-sm text-red-600">{errors.billing_cycle.message}</p>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <select
                {...register('payment_method', { required: 'Payment method is required' })}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSubmitting}
              >
                <option value="manual">Manual Payment</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="check">Check</option>
                <option value="wire_transfer">Wire Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="other">Other</option>
              </select>
              {errors.payment_method && (
                <p className="mt-1 text-sm text-red-600">{errors.payment_method.message}</p>
              )}
            </div>

            {/* Transaction ID */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Transaction ID (Optional)
              </label>
              <Input
                type="text"
                {...register('transaction_id')}
                placeholder="Enter transaction ID or reference number"
                disabled={isSubmitting}
              />
              {errors.transaction_id && (
                <p className="mt-1 text-sm text-red-600">{errors.transaction_id.message}</p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700">
                Notes (Optional)
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Add any payment notes or references"
                disabled={isSubmitting}
              />
              {errors.notes && (
                <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
              )}
            </div>

            {/* Info message - Subscription will be created/upgraded immediately */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> {isNewSubscription 
                  ? `Your subscription for ${upgradePlan?.name} will be created immediately when you complete the payment.`
                  : `Your subscription will be upgraded to ${upgradePlan?.name} immediately when you complete the payment.`
                }
              </p>
            </div>

            {/* Completion Checkbox - Only show for upgrades, not for new subscriptions */}
            {!isNewSubscription && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="mark_completed"
                  {...register('mark_completed')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-secondary-300 rounded"
                  disabled={isSubmitting}
                />
                <label htmlFor="mark_completed" className="text-sm text-secondary-700">
                  Mark payment as completed
                </label>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || calculatedAmount <= 0}
                className="min-w-[120px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Complete Payment'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
